/**
 * Display formatting.
 *
 * The rule everywhere: an unavailable value renders as "N/A" with a `title`
 * carrying the reason. It is never rendered as 0, "-", or an empty cell that
 * could be mistaken for zero (spec section 22).
 */

import type { MetricId, MetricValue } from '@/lib/nutrition/calculations';
import { METRICS } from '@/lib/nutrition/calculations';
import { formatQuantity, trimNumber } from '@/lib/nutrition/units';
import type { Quantity } from '@/types/product';

export const NA = 'N/A';

/** Currency, using the browser's locale but the product's own currency code. */
export function formatCurrency(amount: number | null, currency: string | null): string {
  if (amount === null || !Number.isFinite(amount)) return NA;
  const code = currency ?? 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: amount < 1 ? 3 : 2,
    }).format(amount);
  } catch {
    // Unknown currency code — show the number with the raw code appended.
    return `${trimNumber(amount, 2)} ${code}`;
  }
}

/** The unit string for a metric, with `$` replaced by the real currency. */
export function metricUnit(metric: MetricId, currency: string | null): string {
  return METRICS[metric].unit.replace('$', currency ?? '$');
}

/**
 * Render a metric value with its unit, or "N/A".
 * Money-denominated metrics are formatted as currency; the rest as plain numbers.
 */
export function formatMetric(
  metric: MetricId,
  value: MetricValue | undefined,
  currency: string | null,
  quantityKind: Quantity['kind'] | null = null,
): string {
  if (!value || value.value === null) return NA;
  const meta = METRICS[metric];

  if (metric === 'price') return formatCurrency(value.value, currency);

  if (metric === 'packageSize') {
    return quantityKind === null ? trimNumber(value.value, 0) : formatQuantity(value.value, quantityKind);
  }

  // Metrics whose numerator is money read best as currency.
  if (meta.unit.startsWith('$')) {
    return `${formatCurrency(value.value, currency)} ${meta.unit.replace('$ / ', '/ ')}`.replace(
      / \/ /,
      ' / ',
    );
  }

  return `${trimNumber(value.value, meta.decimals)} ${meta.unit.replace('$', currency ?? '$')}`;
}

/** Compact form for dense grids: the number only, unit shown in the label. */
export function formatMetricValue(
  metric: MetricId,
  value: MetricValue | undefined,
  currency: string | null,
  quantityKind: Quantity['kind'] | null = null,
): string {
  if (!value || value.value === null) return NA;
  if (metric === 'price') return formatCurrency(value.value, currency);
  if (metric === 'packageSize') {
    return quantityKind === null ? trimNumber(value.value, 0) : formatQuantity(value.value, quantityKind);
  }
  if (METRICS[metric].unit.startsWith('$')) return formatCurrency(value.value, currency);
  return trimNumber(value.value, METRICS[metric].decimals);
}

/** The tooltip for a metric: either its formula, or why it is unavailable. */
export function metricTitle(metric: MetricId, value: MetricValue | undefined): string {
  if (value?.unavailableReason) return value.unavailableReason;
  return `${METRICS[metric].label}: ${METRICS[metric].formula}`;
}

export function formatPercent(value: number | null, decimals = 0): string {
  return value === null || !Number.isFinite(value) ? NA : `${trimNumber(value, decimals)}%`;
}

/** Signed percentage, for price change. */
export function formatSignedPercent(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return NA;
  const sign = value > 0 ? '+' : '';
  return `${sign}${trimNumber(value, 1)}%`;
}

export function formatPackage(quantity: Quantity | null): string {
  if (quantity === null) return NA;
  const base = formatQuantity(quantity.netAmount, quantity.kind);
  return quantity.packCount > 1
    ? `${base} (${quantity.packCount} × ${formatQuantity(quantity.netAmount / quantity.packCount, quantity.kind)})`
    : base;
}

export function formatDate(iso: string | null): string {
  if (iso === null) return NA;
  const t = Date.parse(iso);
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : NA;
}

/** Human name for a provider slug, used in the data-source line. */
export function providerLabel(id: string): string {
  switch (id) {
    case 'buywhere':
      return 'BuyWhere';
    case 'openfoodfacts':
      return 'Open Food Facts';
    case 'mock':
      return 'Sample catalog';
    case 'user':
      return 'You';
    default:
      return id;
  }
}
